// @ts-strict-ignore
import { useCallback, useEffect, useState, useMemo } from 'react';

import { Box } from '@mui/material';

import { css, styled } from '@mui/material/styles';
import { AnalyticEventAction } from 'analytics';
import { Dialogs } from 'analytics/events/dialog';
import {
  trackDropdownInputUsageAnalyticsEvent,
  trackInputUsageAnalyticsEvent,
  trackOpenTextInputUsageAnalyticsEvent
} from 'analytics/events/input-usage';
import classNames from 'classnames';

import { NationalNumber } from 'libphonenumber-js';

import { isEmpty, isNil } from 'lodash/fp';

import { useFormContext } from 'react-hook-form';
import { useUpdateEffect } from 'react-use';

import { useStores } from 'mobx/hooks/useStores';

import { formatDate, getTodayFormattedDate } from 'utils/DateUtils';

import { getNumberAndCountryCodeFromPhone } from 'utils/PhoneUtils';

import { FEATURES } from 'constants/features';

import Patient, {
  callbackRelationshipOptions,
  IPatientContact,
  Language,
  languageOptions,
  languageOptionsMap
} from 'models/Patient';
import { PhoneType } from 'models/PhoneNumberDetails';
import { TicketTypeCreationAllowance, TicketTypeKind } from 'models/TicketTypes';

import { RHFStyledInput } from 'views/Widgets/StyledInput';
import StyledPhoneInput from 'views/Widgets/StyledPhoneInput';

import { MessagingConsentFormRadioGroup } from 'components/Ticket/TicketForms/MessagingConsentFormRadioGroup';
import { VerificationStatusPanel } from 'components/Ticket/TicketForms/VerificationStatusPanel';
import { FormLabeledCheckbox } from 'components/UIkit/atoms/Checkbox';
import { MessageDialog } from 'components/UIkit/atoms/Dialog';
import { FormAutocomplete } from 'components/UIkit/atoms/Dropdown';
import { FormRadioGroup } from 'components/UIkit/atoms/RadioButton';
import { Text } from 'components/UIkit/atoms/Text';

import { NEW_CONTACT_VALUE, PATIENT_IS_CONTACT_VALUE, TicketFormField } from './ticket.shared';
import { useConsentVerification } from './useConsentVerification';

const DATE_FORMAT = 'MM/DD/YYYY';

interface IOperatorTicketContactFormProps {
  patient: Patient;
  originalContactHippaAuthDate?: Date;
  originalPatientPhone?: string;
  namePrefix?: string;
}

export const OperatorTicketContactForm = ({
  patient,
  originalContactHippaAuthDate,
  originalPatientPhone,
  namePrefix = ''
}: IOperatorTicketContactFormProps) => {
  const [hipaaDate, setHipaaDate] = useState(getTodayFormattedDate(DATE_FORMAT));
  const [showLandlineDialog, setShowLandlineDialog] = useState(false);
  const [showVerificationPanel, setShowVerificationPanel] = useState(false);
  const [shouldAutoSendOnMount, setShouldAutoSendOnMount] = useState(false);

  const { register, setValue, watch, clearErrors, formState, getValues } = useFormContext();
  const { settingsStore, ticketTypesStore } = useStores();
  const { getTicketTypeById } = ticketTypesStore;
  const { errors } = formState;

  const {
    resetVerificationFlow,
    isLandline: isCurrentContactLandline,
    isNewContactInfoIncomplete,
    createContactIfNeeded
  } = useConsentVerification();

  const {
    contactId,
    countryCode,
    callbackName,
    phone,
    callbackRelationship,
    callbackHippaAuth,
    messagingConsent,
    tickets,
    ticket
  } = watch();

  // Feature flags
  const isConsentVerificationEnabled = settingsStore.hasFeature(
    FEATURES.MESSAGING_CONSENT_VERIFICATION
  );
  const hasAlternateTextFeature = settingsStore.hasFeature(FEATURES.ALTERNATE_PHONE_TEXT_CHANGE);

  // Messaging enabled check - handle both create form (tickets array) and edit form (single ticket)
  const checkTicketMessagingEnabled = (ticketField: TicketFormField) => {
    const isLocationMessagingEnabled = Boolean(ticketField.locationId?.value.isMessagingEnabled);

    if (!isLocationMessagingEnabled) {
      return false;
    }

    if (isEmpty(ticketField.ticketSubTypeIds)) {
      return false;
    }

    const parentTicket = ticketTypesStore.getOperatorTicketTypeById(ticketField.parentId);

    if (parentTicket && parentTicket?.kind === TicketTypeKind.symptom) {
      return false;
    }

    const [ticketType] = ticketField.ticketSubTypeIds;
    const subTypeNode = getTicketTypeById(Number(ticketType));

    return subTypeNode?.displaySettings?.includes(TicketTypeCreationAllowance.MESSAGING);
  };

  const isMessagingEnabledInAnyTicketByLocationOrType =
    // Create form: check tickets array
    tickets?.some(checkTicketMessagingEnabled) ||
    // Edit form: check single ticket
    (ticket && checkTicketMessagingEnabled(ticket));

  const isPatientMessagingFeatureEnabled =
    (settingsStore.hasFeature(FEATURES.ACTIONS_SEND_PATIENT_SMS) ||
      settingsStore.hasFeature(FEATURES.PATIENT_MESSAGING)) &&
    isMessagingEnabledInAnyTicketByLocationOrType;

  const contactIdAsNumber = parseInt(contactId);

  // Track when user EXPLICITLY selects yes (not on form load)
  useUpdateEffect(
    function updateVerificationPanelVisualAndCheckPhoneType() {
      if (isConsentVerificationEnabled && messagingConsent === 'true') {
        // Check for landline before moving to verification panel
        handleLandlineCheckBeforeVerification();
      } else if (messagingConsent !== 'true') {
        setShowVerificationPanel(false);
      }
    },
    [messagingConsent]
  );

  const handleLandlineCheckBeforeVerification = useCallback(async () => {
    const currentFormPhone = `${countryCode}${phone}`;
    const patientIsContact = contactId === PATIENT_IS_CONTACT_VALUE;

    // Get original phone number
    const originalPhone = patientIsContact
      ? patient?.phone
      : patient?.getContactById(Number(contactId))?.phoneNumber;

    // If phone number was NOT edited, check current contact type immediately
    if (currentFormPhone === originalPhone) {
      if (isCurrentContactLandline) {
        setShowLandlineDialog(true);
        setValue('messagingConsent', null);
        return;
      }
      // Phone not edited and not landline - proceed to verification panel
      setShowVerificationPanel(true);
      setShouldAutoSendOnMount(true);
    } else {
      // Phone was edited, create/update contact first
      try {
        const resolvedContactId = await createContactIfNeeded();

        // Check updated contact type
        const updatedContactPhoneType = patientIsContact
          ? patient.phoneType
          : patient.getContactById(Number(resolvedContactId))?.phoneType;

        if (updatedContactPhoneType === PhoneType.landline) {
          setShowLandlineDialog(true);
          setValue('messagingConsent', null);
          return;
        }

        // Contact created/updated and not landline - proceed to verification panel
        // Update the contactId in form if new contact was created
        if (contactId === NEW_CONTACT_VALUE && resolvedContactId) {
          setValue('contactId', String(resolvedContactId));
        }
        setShowVerificationPanel(true);
        setShouldAutoSendOnMount(true);
      } catch (error) {
        // Reset messaging consent on error and rethrow
        setValue('messagingConsent', null);
        throw error;
      }
    }
  }, [
    countryCode,
    phone,
    contactId,
    patient,
    isCurrentContactLandline,
    setValue,
    createContactIfNeeded
  ]);

  // Check on component mount if verification panel should be shown immediately
  useEffect(
    function checkVerificationStatusOnLoad() {
      if (!isConsentVerificationEnabled) {
        return;
      }

      // If patient has verification status but no definitive consent response, show verification panel
      const hasVerificationStatus = Boolean(patient?.requestConsentMessageStatus);
      const hasDefinitiveResponse = !isNil(patient?.isMessagingConsented);

      if (hasVerificationStatus && !hasDefinitiveResponse) {
        setShowVerificationPanel(true);
        // Don't auto-send on mount when showing due to existing status
        setShouldAutoSendOnMount(false);
      }
    },
    [
      isConsentVerificationEnabled,
      patient?.requestConsentMessageStatus,
      patient?.isMessagingConsented
    ]
  );

  const handleChangeContact = useCallback(() => {
    setValue('messagingConsent', null);
    setShowVerificationPanel(false);
    setShouldAutoSendOnMount(false);
    resetVerificationFlow();
  }, [setValue, resetVerificationFlow]);

  // set hipaa checkbox date
  const setHippaDateBySelection = useCallback((date?: Date) => {
    const newDate = date ? formatDate(date, DATE_FORMAT) : getTodayFormattedDate(DATE_FORMAT);
    setHipaaDate(newDate);
  }, []);

  // set hipaa checkbox and hipaa date on contact fields change
  useUpdateEffect(
    function setHippaCheckAndDate() {
      let hippaChecked = false;
      const editMode = !!originalContactHippaAuthDate;
      const existContact =
        contactId !== PATIENT_IS_CONTACT_VALUE && contactId !== NEW_CONTACT_VALUE;

      if (editMode && existContact) {
        const contact = patient.contacts.find((contact) => contact.id === contactIdAsNumber);
        if (contact) {
          hippaChecked =
            contact.id === contactIdAsNumber &&
            contact.name === callbackName &&
            contact.phoneNumber === `${countryCode}${phone}` &&
            contact.relationship === callbackRelationship?.value &&
            getValues('callbackHippaAuth');
        }

        setValue('callbackHippaAuth', hippaChecked);
        setHippaDateBySelection(hippaChecked ? originalContactHippaAuthDate : null);
      } else {
        setValue('callbackHippaAuth', hippaChecked);
      }
    },
    [
      contactId,
      patient?.contacts,
      contactIdAsNumber,
      countryCode,
      callbackName,
      phone,
      callbackRelationship,
      originalContactHippaAuthDate,
      setValue,
      setHippaDateBySelection
    ]
  );

  useEffect(
    function onContactChange() {
      clearErrors(['phone', 'callbackName', 'callbackRelationship', 'language']);
      let countryCode: string = null;
      let phoneNumber: NationalNumber = null;
      let name: string = null;
      let relationship: string = null;
      let phoneExtension: string = null;
      let language: string;
      if (contactId === PATIENT_IS_CONTACT_VALUE) {
        // Set phone number if contact is patient himself
        // In edit mode - originalContactPhoneNumber should be used (can be different from patient.phone)
        const patientPhone = originalPatientPhone || patient?.phone;
        [countryCode, phoneNumber] = getNumberAndCountryCodeFromPhone(patientPhone);
        phoneExtension = patient.phoneExtension;
        language = patient.language;
      } else if (contactId === NEW_CONTACT_VALUE) {
        // Reset fields for new contact
        [countryCode, phoneNumber] = ['+1', null];
        language = Language.English;
      } else {
        // Get details from contact
        const contact = patient.contacts?.find((contact) => contact.id === contactIdAsNumber);
        [countryCode, phoneNumber] = getNumberAndCountryCodeFromPhone(contact?.phoneNumber);
        [name, relationship] = [contact?.name, contact?.relationship];
        phoneExtension = contact?.phoneExtension;
        language = contact?.language;
      }

      setValue('phone', phoneNumber || '');
      setValue('countryCode', countryCode);
      setValue('callbackName', name);
      setValue('phoneExtension', phoneExtension);
      setValue(
        'language',
        languageOptions.find((option) => option.value === language) || {
          value: Language.English,
          label: languageOptionsMap[Language.English]
        }
      );
      setValue(
        'callbackRelationship',
        relationship ? { value: relationship, label: relationship } : null
      );
    },
    [contactId, clearErrors, patient, setValue, contactIdAsNumber, originalPatientPhone]
  );

  const contactOptions = useMemo(() => {
    const patientOption = {
      value: PATIENT_IS_CONTACT_VALUE,
      label: `${patient?.fullName} (Patient)`,
      id: `${namePrefix}${patient?.fullName}`
    };

    const newContactOption = {
      value: NEW_CONTACT_VALUE,
      label: hasAlternateTextFeature ? 'Alternate Number' : 'Someone Else',
      id: `${namePrefix}Someone Else`
    };

    let contactOptions = [newContactOption, patientOption];

    if (patient?.contacts) {
      contactOptions = [
        ...contactOptions,
        ...patient.contacts
          .filter((contact) => contact.id === Number(contactId) || !contact.deletedAt)
          .map((contact: IPatientContact) => ({
            value: contact.id.toString(),
            label: `${contact.name} (${contact.relationship})`,
            id: `${namePrefix}${contact.id}`
          }))
      ];
    }

    return contactOptions;
  }, [hasAlternateTextFeature, contactId, namePrefix, patient?.contacts, patient?.fullName]);

  useUpdateEffect(
    function trackCallbackHippaAnalyticsEvent() {
      trackInputUsageAnalyticsEvent({
        action: callbackHippaAuth ? AnalyticEventAction.Add : AnalyticEventAction.Remove,
        value: 'Callback Hippa',
        type: 'checkbox'
      });
    },
    [callbackHippaAuth]
  );

  useUpdateEffect(
    function trackCallbackContactAnalyticsEvent() {
      trackInputUsageAnalyticsEvent({
        action: AnalyticEventAction.Update,
        value: 'Callback Contact',
        type: 'radio'
      });
    },
    [contactId]
  );

  const getBaseFields = () => {
    return (
      <>
        <StyledPhoneInputContainer display="flex" gap={8} alignItems="flex-end">
          <StyledPhoneInput
            label="Phone"
            error={Boolean(errors.phone)}
            disabled={contactId === PATIENT_IS_CONTACT_VALUE}
            onBlur={(_, valueAfterFocus, currentValue) => {
              trackOpenTextInputUsageAnalyticsEvent(
                currentValue,
                valueAfterFocus,
                'Callback Phone Number'
              );
            }}
          />
          <RHFStyledInput
            name="phoneExtension"
            error={Boolean(errors.phoneExtension)}
            register={register}
            placeholder="Extension"
            maxLength={20}
            validate={(v) => !v || /^[a-zA-Z0-9]+$/.test(v)}
          />
        </StyledPhoneInputContainer>
        <FormAutocomplete
          label="Language Preference"
          isRequired
          name="language"
          options={languageOptions}
          isClearable={false}
          onChange={(_, actionMeta, eventKey) =>
            trackDropdownInputUsageAnalyticsEvent(
              actionMeta,
              'Language Preference',
              eventKey === 'Enter'
            )
          }
        />
      </>
    );
  };

  const getContactUpdateFields = () => {
    return (
      <Box display="flex" flexDirection="column" gap={20}>
        <StyledContactInfoContainer display="flex" gap={8} alignItems="flex-end">
          <StyledRHFStyledInput
            showErrorOutside
            isRequired
            name="callbackName"
            label="Contact"
            placeholder="Name"
            register={register}
            error={Boolean(errors.callbackName)}
            getValues={getValues}
            onBlur={(_, currentValue, valueAfterFocus) =>
              trackOpenTextInputUsageAnalyticsEvent(currentValue, valueAfterFocus, 'Callback Name')
            }
          />
          <FormAutocomplete
            isRequired
            placeholder="Relationship"
            name="callbackRelationship"
            options={callbackRelationshipOptions}
            isClearable={false}
            onChange={(_, actionMeta, eventKey) =>
              trackDropdownInputUsageAnalyticsEvent(
                actionMeta,
                'Relationship to Patient',
                eventKey === 'Enter'
              )
            }
          />
        </StyledContactInfoContainer>
        {getBaseFields()}
        <FormLabeledCheckbox
          name="callbackHippaAuth"
          id={`${namePrefix}callbackHippaAuth`}
          label={`I've verified that this person is HIPAA-authorized as of ${hipaaDate}`}
        />
      </Box>
    );
  };

  const renderAdditionalFields = () => {
    if (contactId === PATIENT_IS_CONTACT_VALUE) {
      return (
        <Box display="flex" flexDirection="column" gap={20}>
          {getBaseFields()}
        </Box>
      );
    }
    return getContactUpdateFields();
  };

  const handleCloseLandlineDialog = useCallback(() => {
    setShowLandlineDialog(false);
  }, []);

  const contactsContainerClassNames = classNames('field contact-selection', {
    small: namePrefix === 'edit'
  });

  const showConsentSection = isPatientMessagingFeatureEnabled;
  const showForConsentFlow = isConsentVerificationEnabled && isNewContactInfoIncomplete === false;

  // Determine whether to show the messaging consent form based on feature flags
  const shouldShowMessagingConsentForm = isConsentVerificationEnabled
    ? showForConsentFlow && showConsentSection // When verification is enabled, require both conditions
    : showConsentSection; // When verification is disabled, only check the base condition

  // Verification panel visibility
  const shouldShowVerificationPanel = useMemo(() => {
    if (!isConsentVerificationEnabled) {
      return false;
    }

    return showVerificationPanel;
  }, [isConsentVerificationEnabled, showVerificationPanel]);

  return (
    <Box flex={1} minWidth={0}>
      {isConsentVerificationEnabled && (
        <MessageDialog
          id={Dialogs.GeneralError}
          isOpen={showLandlineDialog}
          title="Cannot Verify Consent via Landline"
          handleClose={handleCloseLandlineDialog}
          primaryActionProps={{
            text: 'OK',
            onClick: handleCloseLandlineDialog
          }}
        >
          Messaging consent is verified via text message. Please update to a mobile number in order
          to proceed.
        </MessageDialog>
      )}

      {shouldShowVerificationPanel ? (
        <VerificationStatusPanel
          onChangeContact={handleChangeContact}
          shouldAutoSendOnMount={shouldAutoSendOnMount}
        />
      ) : (
        <>
          <Text variant="h4" component="h4">
            Who should we get back to?
          </Text>
          <Box pt={20}>
            <input {...register('countryCode')} type="hidden" />
            <div className={contactsContainerClassNames}>
              <FormRadioGroup name="contactId" options={contactOptions} />
            </div>
          </Box>
          <StyledSeparator />
          {renderAdditionalFields()}

          {shouldShowMessagingConsentForm && (
            <>
              <StyledSeparator />
              <MessagingConsentFormRadioGroup />
            </>
          )}
        </>
      )}
    </Box>
  );
};
const StyledContactInfoContainer = styled(Box)`
  .styled-input {
    max-width: 185px;
    .input-area {
      .form-control {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
  }

  .styled-select {
    flex: 1;
    .selection-area-container .styled-select__control {
      border-bottom-left-radius: 0;
      border-top-left-radius: 0;
    }
  }
`;

const StyledPhoneInputContainer = styled(Box)`
  .phone-input {
    width: 185px;
    max-width: 185px;
    .phone-input-number-container {
      border-bottom-right-radius: 0;
      border-top-right-radius: 0;
      input {
        width: 110px;
      }
    }
  }

  .styled-input {
    flex: 1;
    .form-control {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }
`;

const StyledSeparator = styled('hr')(
  ({ theme }) => css`
    border-top: 1px solid ${theme.palette.natural.border};
    margin: ${theme.spacing(20)} 0;
  `
);

const StyledRHFStyledInput = styled(RHFStyledInput)(
  ({ theme }) => css`
    &.styled-input {
      .form-control {
        border-radius: 0 ${theme.borderRadius.large} ${theme.borderRadius.large}
          ${theme.borderRadius.large};
      }
    }
  `
);
